import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../contexts/ToastContext";
import { embedWidgetService } from "../../services/embedWidgetService";
import { businessProfileService } from "../../services/businessProfileService";
import type {
  EmbedTokenSummary,
  EmbedTokenCreated,
  EmbedExposedItem,
  EmbedOriginStats,
} from "../../types/embedWidget";

// Picked up from VITE_EMBED_LOADER_URL at build time; defaults to the production
// host. On dev the build workflow sets this to https://dev.embed.spotto.pl/v1/loader.js.
const EMBED_LOADER_URL =
  (import.meta.env.VITE_EMBED_LOADER_URL as string | undefined) ??
  "https://embed.spotto.pl/v1/loader.js";

export default function EmbedWidgetPage() {
  const { t } = useTranslation();
  const { token } = useAuth();
  const { showToast } = useToast();

  const [tokens, setTokens] = useState<EmbedTokenSummary[] | null>(null);
  const [exposed, setExposed] = useState<EmbedExposedItem[] | null>(null);
  const [analytics, setAnalytics] = useState<EmbedOriginStats[] | null>(null);
  const [businessId, setBusinessId] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [justCreated, setJustCreated] = useState<EmbedTokenCreated | null>(null);
  const [selectedTokenId, setSelectedTokenId] = useState<string | null>(null);
  const [previewAccent, setPreviewAccent] = useState<string>("#4D63AC");
  const [previewTheme, setPreviewTheme] = useState<"light" | "dark">("light");
  const [previewMode, setPreviewMode] = useState<"all" | "bookings" | "shop">("all");

  useEffect(() => {
    if (!token) return;
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  async function reload() {
    if (!token) return;
    try {
      const [t1, e1, a1, p1] = await Promise.all([
        embedWidgetService.listTokens(token),
        embedWidgetService.listExposed(token),
        embedWidgetService.getAnalytics(token, 30),
        businessProfileService.getProfile(token).catch(() => null),
      ]);
      setTokens(t1);
      setExposed(e1);
      setAnalytics(a1);
      setBusinessId(p1?.id ?? null);
      if (selectedTokenId == null && t1.length > 0) setSelectedTokenId(t1[0].id);
    } catch {
      setLoadError(t("common.error"));
    }
  }

  async function onCreate(req: { name: string; allowedOrigins: string[]; isTestMode: boolean }) {
    if (!token) return;
    try {
      const created = await embedWidgetService.createToken(token, req);
      setJustCreated(created);
      setSelectedTokenId(created.id);
      await reload();
    } catch (e) {
      showToast?.("error", t("common.error"), describeError(e));
    }
  }

  async function onRevoke(tokenId: string) {
    if (!token) return;
    if (!confirm(t("embedWidget.tokens.confirmRevoke"))) return;
    try {
      await embedWidgetService.revokeToken(token, tokenId);
      await reload();
    } catch (e) {
      showToast?.("error", t("common.error"), describeError(e));
    }
  }

  async function onToggleExposed(item: EmbedExposedItem) {
    if (!token) return;
    try {
      await embedWidgetService.setExposed(token, {
        itemId: item.itemId,
        type: item.type,
        exposed: !item.exposed,
      });
      await reload();
    } catch (e) {
      showToast?.("error", t("common.error"), describeError(e));
    }
  }

  const selectedToken = useMemo(
    () => tokens?.find((tk) => tk.id === selectedTokenId) ?? null,
    [tokens, selectedTokenId]
  );

  if (loadError) return <Center>{loadError}</Center>;
  if (tokens === null || exposed === null || analytics === null) return <Center>{t("common.loading")}</Center>;

  const facilities = exposed.filter((e) => e.type === "Facility");
  const products = exposed.filter((e) => e.type === "Product");

  return (
    <main className="p-6 max-w-4xl mx-auto">
      <h1 className="font-display text-3xl font-light tracking-tight m-0 mb-6 text-ink-primary">
        {t("embedWidget.pageTitlePrefix")}{' '}
        <span className="font-mono uppercase tracking-[0.12em] text-brand-primary font-semibold text-2xl align-middle">
          Plugin Commerce
        </span>
      </h1>

      {/* Tokens */}
      <Section title={t("embedWidget.sections.tokens")}>
        {tokens.length === 0 ? (
          <p className="text-ink-muted text-sm m-0">{t("embedWidget.tokens.emptyHint")}</p>
        ) : (
          <ul className="list-none p-0 m-0 grid gap-2">
            {tokens.map((tk) => (
              <li
                key={tk.id}
                className="flex gap-3 items-center bg-surface-card-hover border border-border-subtle rounded-lg px-3 py-2.5"
              >
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-ink-primary">
                    {tk.name}{" "}
                    <span className="font-mono text-ink-muted text-xs">…{tk.tokenSuffix}</span>
                    {tk.isTestMode && (
                      <span className="inline-block px-1.5 py-px ml-2 bg-brand-primary-light text-white text-[10px] font-semibold rounded uppercase tracking-wider">
                        {t("embedWidget.tokens.badgeTest")}
                      </span>
                    )}
                    {tk.revokedAt && (
                      <span className="inline-block px-1.5 py-px ml-2 bg-brand-warm text-white text-[10px] font-semibold rounded uppercase tracking-wider">
                        {t("embedWidget.tokens.badgeRevoked")}
                      </span>
                    )}
                  </div>
                  <div className="text-ink-muted text-sm">
                    {tk.allowedOrigins.length === 0
                      ? t("embedWidget.tokens.noOrigins")
                      : tk.allowedOrigins.join(", ")}
                  </div>
                  <div className="text-ink-muted text-[11px]">
                    {t("embedWidget.tokens.createdAt", { date: formatDate(tk.createdAt) })}
                    {tk.lastUsedAt && ` · ${t("embedWidget.tokens.lastUsed", { date: formatDate(tk.lastUsedAt) })}`}
                  </div>
                </div>
                <div className="flex gap-1.5">
                  <button
                    type="button"
                    onClick={() => setSelectedTokenId(tk.id)}
                    disabled={!!tk.revokedAt}
                    className={
                      selectedTokenId === tk.id
                        ? "px-2.5 py-1.5 rounded text-xs cursor-pointer bg-brand-primary text-white border border-brand-primary disabled:opacity-50 disabled:cursor-not-allowed"
                        : "px-2.5 py-1.5 rounded text-xs cursor-pointer bg-surface-card text-ink-primary border border-border-strong hover:bg-surface-card-hover disabled:opacity-50 disabled:cursor-not-allowed"
                    }
                  >
                    {selectedTokenId === tk.id ? t("embedWidget.tokens.selected") : t("embedWidget.tokens.useForSnippet")}
                  </button>
                  {!tk.revokedAt && (
                    <button
                      type="button"
                      onClick={() => onRevoke(tk.id)}
                      className="px-2.5 py-1.5 rounded text-xs cursor-pointer bg-surface-card border border-brand-warm text-brand-warm hover:bg-surface-card-hover"
                    >
                      {t("embedWidget.tokens.revoke")}
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}

        <CreateTokenForm onCreate={onCreate} />

        {justCreated && (
          <div className="mt-4 p-3 bg-brand-confirm/10 border border-brand-confirm rounded-lg grid gap-2">
            <strong className="text-ink-primary">
              {t("embedWidget.tokens.freshTokenWarn")}
            </strong>
            <input
              readOnly
              value={justCreated.rawToken}
              onFocus={(e) => e.currentTarget.select()}
              className="px-3 py-2 bg-surface-card border border-border-strong rounded-lg text-sm text-ink-primary font-mono w-full"
            />
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => copyToClipboard(justCreated.rawToken)}
                className="px-2.5 py-1.5 rounded text-xs cursor-pointer bg-surface-card text-ink-primary border border-border-strong hover:bg-surface-card-hover"
              >
                {t("embedWidget.tokens.copy")}
              </button>
              <button
                type="button"
                onClick={() => setJustCreated(null)}
                className="px-2.5 py-1.5 rounded text-xs cursor-pointer bg-surface-card text-ink-primary border border-border-strong hover:bg-surface-card-hover"
              >
                {t("embedWidget.tokens.done")}
              </button>
            </div>
          </div>
        )}
      </Section>

      {/* Exposed items */}
      <Section title={t("embedWidget.sections.exposed")}>
        <p className="text-ink-muted text-sm m-0">{t("embedWidget.exposed.hint")}</p>
        <ExposedColumn title={t("embedWidget.exposed.facilities")} items={facilities} onToggle={onToggleExposed} />
        <ExposedColumn title={t("embedWidget.exposed.products")} items={products} onToggle={onToggleExposed} />
      </Section>

      {/* Theme preview */}
      <Section title={t("embedWidget.sections.themePreview")}>
        <p className="text-ink-muted text-sm m-0 mb-2">{t("embedWidget.theme.hint")}</p>
        <ThemePreview
          theme={previewTheme}
          accent={previewAccent}
          onThemeChange={setPreviewTheme}
          onAccentChange={setPreviewAccent}
        />
      </Section>

      {/* Per-origin analytics */}
      <Section title={t("embedWidget.sections.analytics")}>
        {analytics.length === 0 ? (
          <p className="text-ink-muted text-sm m-0">{t("embedWidget.analytics.empty")}</p>
        ) : (
          <AnalyticsTable rows={analytics} />
        )}
      </Section>

      {/* Install snippet */}
      <Section title={t("embedWidget.sections.snippet")}>
        {!selectedToken ? (
          <p className="text-ink-muted text-sm m-0">{t("embedWidget.snippet.pickTokenFirst")}</p>
        ) : (
          <InstallSnippet
            token={selectedToken}
            businessId={businessId}
            mode={previewMode}
            onModeChange={setPreviewMode}
            theme={previewTheme}
            accent={previewAccent}
            // Only available while justCreated banner is still on screen.
            rawTokenIfFresh={justCreated?.id === selectedToken.id ? justCreated.rawToken : null}
          />
        )}
      </Section>
    </main>
  );
}

// ---- subcomponents ----

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="bg-surface-card border border-border-subtle rounded-xl p-5 mb-4">
      <h2 className="font-display text-base font-semibold tracking-tight m-0 mb-3 text-ink-primary">{title}</h2>
      {children}
    </section>
  );
}

function CreateTokenForm({
  onCreate,
}: {
  onCreate: (req: { name: string; allowedOrigins: string[]; isTestMode: boolean }) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [originsRaw, setOriginsRaw] = useState("");
  const [testMode, setTestMode] = useState(false);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const origins = originsRaw
      .split(/[\s,]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (!name.trim() || origins.length === 0) return;
    setBusy(true);
    try {
      await onCreate({ name: name.trim(), allowedOrigins: origins, isTestMode: testMode });
      setName("");
      setOriginsRaw("");
      setTestMode(false);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="grid gap-2 mt-4">
      <input
        type="text"
        placeholder={t("embedWidget.createToken.namePlaceholder")}
        value={name}
        onChange={(e) => setName(e.target.value)}
        required
        className="px-3 py-2 bg-surface-card border border-border-strong rounded-lg text-sm text-ink-primary placeholder-ink-muted focus:ring-2 focus:ring-brand-primary focus:border-transparent w-full"
      />
      <input
        type="text"
        placeholder={t("embedWidget.createToken.originsPlaceholder")}
        value={originsRaw}
        onChange={(e) => setOriginsRaw(e.target.value)}
        required
        className="px-3 py-2 bg-surface-card border border-border-strong rounded-lg text-sm text-ink-primary placeholder-ink-muted focus:ring-2 focus:ring-brand-primary focus:border-transparent w-full"
      />
      <label className="flex gap-2 items-center text-sm text-ink-secondary">
        <input
          type="checkbox"
          checked={testMode}
          onChange={(e) => setTestMode(e.target.checked)}
          className="accent-brand-primary"
        />
        {t("embedWidget.createToken.testMode")}
      </label>
      <button
        type="submit"
        disabled={busy}
        className="justify-self-start px-3.5 py-2 bg-brand-primary text-white border-0 rounded-lg text-sm font-semibold cursor-pointer hover:bg-brand-primary-hover disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {t("embedWidget.createToken.submit")}
      </button>
    </form>
  );
}

function ExposedColumn({
  title,
  items,
  onToggle,
}: {
  title: string;
  items: EmbedExposedItem[];
  onToggle: (i: EmbedExposedItem) => void;
}) {
  if (items.length === 0) return null;
  return (
    <div className="mt-3">
      <h3 className="text-xs font-semibold text-ink-secondary m-0 mb-1.5 uppercase tracking-wider">{title}</h3>
      <ul className="list-none p-0 m-0 grid gap-2">
        {items.map((it) => (
          <li
            key={it.itemId}
            className="flex gap-3 items-center bg-surface-card-hover border border-border-subtle rounded-lg px-3 py-2"
          >
            <label className="flex gap-2.5 items-center flex-1 cursor-pointer text-ink-primary">
              <input
                type="checkbox"
                checked={it.exposed}
                onChange={() => onToggle(it)}
                className="accent-brand-primary"
              />
              <span>{it.name}</span>
            </label>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ThemePreview({
  theme,
  accent,
  onThemeChange,
  onAccentChange,
}: {
  theme: "light" | "dark";
  accent: string;
  onThemeChange: (v: "light" | "dark") => void;
  onAccentChange: (v: string) => void;
}) {
  const { t } = useTranslation();
  // Preview is a CSS-only mock — embed.spotto.pl isn't always reachable in
  // every environment, so we don't iframe it. Faux booking card shows the
  // selected theme + accent in action.
  const cardBg = theme === "dark" ? "#23262B" : "#FFFFFF";
  const appBg = theme === "dark" ? "#1A1D24" : "#F0F4F7";
  const ink = theme === "dark" ? "#F5F7FA" : "#1A1D24";
  const inkMuted = theme === "dark" ? "#9AA0A6" : "#6B7280";

  return (
    <div className="grid gap-3 mt-2">
      <div className="flex gap-3 items-center text-sm flex-wrap">
        <label className="flex gap-1.5 items-center cursor-pointer text-ink-primary">
          <input
            type="radio"
            checked={theme === "light"}
            onChange={() => onThemeChange("light")}
            className="accent-brand-primary"
          />
          {t("embedWidget.theme.light")}
        </label>
        <label className="flex gap-1.5 items-center cursor-pointer text-ink-primary">
          <input
            type="radio"
            checked={theme === "dark"}
            onChange={() => onThemeChange("dark")}
            className="accent-brand-primary"
          />
          {t("embedWidget.theme.dark")}
        </label>
        <span className="ml-4 text-ink-muted">{t("embedWidget.theme.accent")}</span>
        <input
          type="color"
          value={accent}
          onChange={(e) => onAccentChange(e.target.value)}
          className="w-9 h-7 border border-border-strong rounded p-0 cursor-pointer bg-transparent"
        />
        <span className="font-mono text-xs text-ink-muted">{accent}</span>
      </div>

      <div
        style={{ background: appBg }}
        className="p-5 border border-border-subtle rounded-xl"
      >
        <div
          style={{ background: cardBg, color: ink }}
          className="border border-border-subtle rounded-xl p-4 max-w-sm mx-auto"
        >
          <div className="font-display text-lg font-semibold mb-1">{t("embedWidget.theme.sampleFacility")}</div>
          <div style={{ color: inkMuted }} className="text-sm mb-3">
            {t("embedWidget.theme.sampleSlot")}
          </div>
          <button
            type="button"
            style={{ background: accent }}
            className="w-full py-2.5 px-3.5 text-white border-0 rounded-xl text-base font-semibold cursor-default"
          >
            {t("embedWidget.theme.samplePayButton")}
          </button>
        </div>
      </div>
    </div>
  );
}

function AnalyticsTable({ rows }: { rows: EmbedOriginStats[] }) {
  const { t } = useTranslation();
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr>
            <Th align="left">{t("embedWidget.analytics.origin")}</Th>
            <Th>{t("embedWidget.analytics.views")}</Th>
            <Th>{t("embedWidget.analytics.bookingStarts")}</Th>
            <Th>{t("embedWidget.analytics.bookingCompletions")}</Th>
            <Th>{t("embedWidget.analytics.purchaseStarts")}</Th>
            <Th>{t("embedWidget.analytics.purchaseCompletions")}</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.origin}>
              <Td align="left">{r.origin || t("embedWidget.analytics.unknownOrigin")}</Td>
              <Td>{r.views}</Td>
              <Td>{r.bookingStarts}</Td>
              <Td>{r.bookingCompletions}</Td>
              <Td>{r.purchaseStarts}</Td>
              <Td>{r.purchaseCompletions}</Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Th({ children, align = "right" }: { children: React.ReactNode; align?: "left" | "right" }) {
  return (
    <th
      className={`px-2 py-1.5 border-b border-border-subtle font-semibold text-ink-secondary text-xs uppercase tracking-wide ${
        align === "left" ? "text-left" : "text-right"
      }`}
    >
      {children}
    </th>
  );
}

function Td({ children, align = "right" }: { children: React.ReactNode; align?: "left" | "right" }) {
  return (
    <td
      className={`px-2 py-2 border-b border-border-subtle text-ink-primary ${
        align === "left" ? "text-left" : "text-right font-mono"
      }`}
    >
      {children}
    </td>
  );
}

function InstallSnippet({
  token,
  businessId,
  mode,
  onModeChange,
  theme,
  accent,
  rawTokenIfFresh,
}: {
  token: EmbedTokenSummary;
  businessId: string | null;
  mode: "all" | "bookings" | "shop";
  onModeChange: (m: "all" | "bookings" | "shop") => void;
  theme: "light" | "dark";
  accent: string;
  rawTokenIfFresh: string | null;
}) {
  const { t } = useTranslation();
  const snippet = buildSnippet({ token, businessId, mode, theme, accent, rawTokenIfFresh });
  const modeLabel: Record<"all" | "bookings" | "shop", string> = {
    all: t("embedWidget.snippet.modeAll"),
    bookings: t("embedWidget.snippet.modeBookings"),
    shop: t("embedWidget.snippet.modeShop"),
  };
  return (
    <>
      <div className="flex gap-3 items-center text-sm mb-2 flex-wrap">
        <span className="text-ink-muted">{t("embedWidget.snippet.mode")}</span>
        {(["all", "bookings", "shop"] as const).map((m) => (
          <label key={m} className="flex gap-1 items-center cursor-pointer text-ink-primary">
            <input
              type="radio"
              name="embed-mode"
              checked={mode === m}
              onChange={() => onModeChange(m)}
              className="accent-brand-primary"
            />
            {modeLabel[m]}
          </label>
        ))}
      </div>
      <textarea
        readOnly
        value={snippet}
        onFocus={(e) => e.currentTarget.select()}
        className="w-full min-h-[140px] p-3 font-mono text-xs bg-surface-card-hover border border-border-subtle rounded-lg text-ink-primary resize-y"
      />
      <button
        type="button"
        onClick={() => copyToClipboard(snippet)}
        className="mt-2 px-2.5 py-1.5 rounded text-xs cursor-pointer bg-surface-card text-ink-primary border border-border-strong hover:bg-surface-card-hover"
      >
        {t("embedWidget.snippet.copy")}
      </button>
      {rawTokenIfFresh ? (
        <p className="text-brand-confirm-deep text-sm mt-2 m-0">
          {t("embedWidget.snippet.freshTokenNote")}
        </p>
      ) : (
        <p className="text-ink-muted text-sm mt-2 m-0">
          {t("embedWidget.snippet.tokenPlaceholderNote")}
        </p>
      )}
    </>
  );
}

function buildSnippet({
  token,
  businessId,
  mode,
  theme,
  accent,
  rawTokenIfFresh,
}: {
  token: EmbedTokenSummary;
  businessId: string | null;
  mode: "all" | "bookings" | "shop";
  theme: "light" | "dark";
  accent: string;
  rawTokenIfFresh: string | null;
}): string {
  // Owner pastes the raw token (we only persist its hash) — but the rest is
  // auto-populated from the portal context so the snippet works as-is for
  // the test mode/theme/accent they're previewing. If they just generated
  // the token in this session, we inline the raw value so they can copy
  // the snippet straight onto their site without an intermediate paste.
  const tokenValue =
    rawTokenIfFresh ?? `emb_pk_${token.isTestMode ? "test" : "live"}_<paste-from-creation-modal>`;
  const lines = [
    `<div id="spotto-embed"></div>`,
    `<script`,
    `  src="${EMBED_LOADER_URL}"`,
    `  data-business-id="${businessId ?? "<your-business-id>"}"`,
    `  data-embed-token="${tokenValue}"`,
    `  data-mode="${mode}"`,
  ];
  if (theme === "dark") lines.push(`  data-theme="dark"`);
  if (accent.toLowerCase() !== "#4d63ac") lines.push(`  data-accent="${accent}"`);
  lines.push(`  async defer`);
  lines.push(`></script>`);
  return lines.join("\n");
}

// ---- helpers ----

async function copyToClipboard(value: string) {
  try {
    await navigator.clipboard.writeText(value);
  } catch {
    // best-effort
  }
}

function describeError(e: unknown): string {
  if (e && typeof e === "object" && "message" in e) return String((e as Error).message);
  return "";
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString();
}

function Center({ children }: { children: React.ReactNode }) {
  return <div className="p-6 text-center text-ink-muted">{children}</div>;
}
